/**
 * Country Context - Provides country-specific configuration throughout the app
 * Fetches config from backend API and manages user country preference
 * Also supports independent currency selection
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Storage keys
const COUNTRY_STORAGE_KEY = 'user_country_preference';
const CURRENCY_STORAGE_KEY = 'user_currency_preference';

// Default country configurations (fallback if API fails)
const DEFAULT_CONFIGS = {
  SL: {
    code: 'SL',
    name: 'Sri Lanka',
    currency: 'LKR',
    currency_symbol: 'Rs.',
    currency_name: 'Sri Lankan Rupee',
    distance_unit: 'km',
    mileage_unit: 'km',
    flag: '🇱🇰'
  },
  UK: {
    code: 'UK',
    name: 'United Kingdom',
    currency: 'GBP',
    currency_symbol: '£',
    currency_name: 'British Pound',
    distance_unit: 'miles',
    mileage_unit: 'miles',
    flag: '🇬🇧'
  },
  EU: {
    code: 'EU',
    name: 'European Union',
    currency: 'EUR',
    currency_symbol: '€',
    currency_name: 'Euro',
    distance_unit: 'km',
    mileage_unit: 'km',
    flag: '🇪🇺'
  }
};

// All available currencies
const ALL_CURRENCIES = [
  { code: 'LKR', symbol: 'Rs.', name: 'Sri Lankan Rupee' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' }
];

// Country to default currency mapping
const COUNTRY_DEFAULT_CURRENCY = {
  SL: 'LKR',
  UK: 'GBP',
  EU: 'EUR'
};

// Per-country page title shown in the browser tab
const COUNTRY_PAGE_TITLE = {
  SL: "Zeilian - Sri Lanka's Marketplace",
  UK: "Zeilian - United Kingdom's Marketplace",
  EU: 'Zeilian - European Marketplace',
};
const DEFAULT_PAGE_TITLE = 'Zeilian - Online Marketplace';

// Create context
const CountryContext = createContext(null);

// Country Provider Component
export const CountryProvider = ({ children }) => {
  const [country, setCountryState] = useState('SL');
  const [countryConfig, setCountryConfig] = useState(DEFAULT_CONFIGS.SL);
  const [allCountries, setAllCountries] = useState([]);
  const [allCurrenciesList, setAllCurrenciesList] = useState(ALL_CURRENCIES);
  const [enabledFields, setEnabledFields] = useState([]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [requiredFields, setRequiredFields] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [filterSchema, setFilterSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Currency state (independent from country)
  const [currency, setCurrencyState] = useState('LKR');
  const [currencyConfig, setCurrencyConfig] = useState(ALL_CURRENCIES[0]);

  // Detect country from timezone
  const detectCountryFromTimezone = useCallback(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneMap = {
        'Asia/Colombo': 'SL',
        'Europe/London': 'UK',
        'Europe/Paris': 'EU',
        'Europe/Berlin': 'EU',
        'Europe/Amsterdam': 'EU',
        'Europe/Madrid': 'EU',
        'Europe/Rome': 'EU',
      };
      const detected = timezoneMap[timezone] || null;
      if (detected && DISABLED_COUNTRIES.has(detected)) {
        return detected === 'EU' ? 'UK' : null;
      }
      return detected;
    } catch {
      return null;
    }
  }, []);

  // Detect country from IP via ipapi.co (free, no key, ~1000/day)
  // Returns 'SL' | 'UK' | 'EU' | null (null = let caller fall back)
  const detectCountryFromIP = useCallback(async () => {
    try {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 4000);
      const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
      clearTimeout(t);
      if (!res.ok) return null;
      const data = await res.json();
      const cc = (data.country_code || '').toUpperCase();
      // Sri Lanka
      if (cc === 'LK') return 'SL';
      // UK / Great Britain
      if (cc === 'GB' || cc === 'UK') return 'UK';
      // European Union member states
      const EU_CC = new Set([
        'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
        'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
      ]);
      if (EU_CC.has(cc)) {
        // EU is currently disabled - fall back to UK for EU visitors
        return DISABLED_COUNTRIES.has('EU') ? 'UK' : 'EU';
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  // Initialize country and currency on mount
  useEffect(() => {
    const initCountry = async () => {
      // Check localStorage for country
      const storedCountry = localStorage.getItem(COUNTRY_STORAGE_KEY);
      const storedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);
      
      if (storedCountry && DEFAULT_CONFIGS[storedCountry]) {
        setCountryState(storedCountry);
        await fetchCountryConfig(storedCountry);
      } else {
        // First visit - detect by IP, then fall back to timezone, then to UK
        let detected = await detectCountryFromIP();
        if (!detected || !DEFAULT_CONFIGS[detected]) {
          const tzGuess = detectCountryFromTimezone();
          // Timezone helper defaults to 'SL' which used to be the primary market.
          // Per product decision: unknown / unsupported → UK.
          detected = tzGuess && DEFAULT_CONFIGS[tzGuess] ? tzGuess : 'UK';
        }
        setCountryState(detected);
        localStorage.setItem(COUNTRY_STORAGE_KEY, detected);
        await fetchCountryConfig(detected);
      }
      
      // Initialize currency - use stored or default for country
      const finalCountry = localStorage.getItem(COUNTRY_STORAGE_KEY) || 'UK';
      if (storedCurrency) {
        const currencyObj = ALL_CURRENCIES.find(c => c.code === storedCurrency);
        if (currencyObj) {
          setCurrencyState(storedCurrency);
          setCurrencyConfig(currencyObj);
        }
      } else {
        // Set default currency for the country
        const defaultCurrency = COUNTRY_DEFAULT_CURRENCY[finalCountry] || 'LKR';
        const currencyObj = ALL_CURRENCIES.find(c => c.code === defaultCurrency);
        if (currencyObj) {
          setCurrencyState(defaultCurrency);
          setCurrencyConfig(currencyObj);
          localStorage.setItem(CURRENCY_STORAGE_KEY, defaultCurrency);
        }
      }
      
      // Also fetch all countries and currencies
      await fetchAllCountries();
      const currencies = await fetchAllCurrencies();
      setAllCurrenciesList(currencies);
    };

    initCountry();
  }, [detectCountryFromTimezone, detectCountryFromIP]);

  // Keep the <title> in sync with the selected country
  useEffect(() => {
    document.title = COUNTRY_PAGE_TITLE[country] || DEFAULT_PAGE_TITLE;
  }, [country]);

  // Fetch country configuration from backend
  const fetchCountryConfig = async (countryCode) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/listings/config/country/${countryCode}`);
      
      if (response.data.success) {
        const data = response.data;
        
        setCountryConfig({
          ...data.country,
          flag: DEFAULT_CONFIGS[countryCode]?.flag || '🌍'
        });
        setEnabledFields(data.enabled_fields || []);
        setDisabledFields(data.disabled_fields || []);
        setRequiredFields(data.required_fields || []);
        setVehicleTypes(data.vehicle_types || []);
        setFilterSchema(data.filter_schema || null);
      } else {
        throw new Error('Failed to fetch country config');
      }
    } catch (err) {
      console.error('Error fetching country config:', err);
      setError(err.message);
      // Use default config as fallback
      setCountryConfig(DEFAULT_CONFIGS[countryCode] || DEFAULT_CONFIGS.SL);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all available countries (from admin-managed settings)
// Countries currently disabled - easy single-line toggle to re-enable later.
// To re-enable EU: change to `const DISABLED_COUNTRIES = new Set([]);`
const DISABLED_COUNTRIES = new Set(['EU']);

const filterEnabledCountries = (list) => {
  if (!Array.isArray(list)) return [];
  return list.filter(c => !DISABLED_COUNTRIES.has(c.code));
};

  const fetchAllCountries = async () => {
    try {
      // First try to fetch from admin localization API (enabled countries only)
      const response = await axios.get(`${API_URL}/api/admin/localization/public/countries`);
      
      if (response.data.success && response.data.countries?.length > 0) {
        setAllCountries(filterEnabledCountries(response.data.countries));
        return;
      }
    } catch (err) {
      console.log('Admin localization not available, trying fallback...');
    }
    
    // Fallback to listings config API
    try {
      const response = await axios.get(`${API_URL}/api/listings/config/countries`);
      
      if (response.data.success) {
        const countries = response.data.countries.map(c => ({
          ...c,
          flag: DEFAULT_CONFIGS[c.code]?.flag || '🌍'
        }));
        setAllCountries(filterEnabledCountries(countries));
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
      // Use default countries
      setAllCountries(filterEnabledCountries(Object.values(DEFAULT_CONFIGS)));
    }
  };

  // Fetch all available currencies (from admin-managed settings)
  const fetchAllCurrencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/localization/public/currencies`);
      
      if (response.data.success && response.data.currencies?.length > 0) {
        return response.data.currencies;
      }
    } catch (err) {
      console.log('Using default currencies');
    }
    return ALL_CURRENCIES;
  };

  // Change country - also updates currency to country default
  const setCountry = useCallback(async (countryCode, updateCurrency = true) => {
    if (countryCode === country) return;
    
    setCountryState(countryCode);
    localStorage.setItem(COUNTRY_STORAGE_KEY, countryCode);
    await fetchCountryConfig(countryCode);
    
    // Update currency to country's default if requested
    if (updateCurrency) {
      const defaultCurrency = COUNTRY_DEFAULT_CURRENCY[countryCode] || 'LKR';
      const currencyObj = ALL_CURRENCIES.find(c => c.code === defaultCurrency);
      if (currencyObj) {
        setCurrencyState(defaultCurrency);
        setCurrencyConfig(currencyObj);
        localStorage.setItem(CURRENCY_STORAGE_KEY, defaultCurrency);
      }
    }
  }, [country]);

  // Change currency independently
  const setCurrency = useCallback((currencyCode) => {
    if (currencyCode === currency) return;
    
    const currencyObj = ALL_CURRENCIES.find(c => c.code === currencyCode);
    if (currencyObj) {
      setCurrencyState(currencyCode);
      setCurrencyConfig(currencyObj);
      localStorage.setItem(CURRENCY_STORAGE_KEY, currencyCode);
    }
  }, [currency]);

  // Format currency using selected currency (not country default)
  const formatCurrency = useCallback((amount) => {
    if (!amount && amount !== 0) return '';
    
    // Use the selected currency, not the country's default
    const symbol = currencyConfig.symbol || countryConfig.currency_symbol;
    const code = currencyConfig.code || countryConfig.currency;
    
    // Format number with thousands separator
    const formatted = new Intl.NumberFormat('en', {
      maximumFractionDigits: 0
    }).format(amount);
    
    // Add currency symbol based on currency format
    if (code === 'GBP' || code === 'EUR' || code === 'USD') {
      return `${symbol}${formatted}`;
    }
    return `${symbol} ${formatted}`;
  }, [currencyConfig, countryConfig]);

  // Format distance/mileage
  const formatMileage = useCallback((value, sourceUnit = 'km') => {
    if (!value && value !== 0) return '';
    
    const targetUnit = countryConfig.mileage_unit || 'km';
    let convertedValue = value;
    
    // Convert if units differ
    if (sourceUnit === 'km' && targetUnit === 'miles') {
      convertedValue = Math.round(value * 0.621371);
    } else if (sourceUnit === 'miles' && targetUnit === 'km') {
      convertedValue = Math.round(value * 1.60934);
    }
    
    const formatted = new Intl.NumberFormat('en').format(convertedValue);
    return `${formatted} ${targetUnit}`;
  }, [countryConfig]);

  // Check if field is enabled
  const isFieldEnabled = useCallback((fieldKey) => {
    if (disabledFields.includes(fieldKey)) return false;
    if (enabledFields.includes(fieldKey)) return true;
    return true; // Default to enabled
  }, [enabledFields, disabledFields]);

  // Check if field is required
  const isFieldRequired = useCallback((fieldKey) => {
    return requiredFields.includes(fieldKey);
  }, [requiredFields]);

  // Get mileage label
  const getMileageLabel = useCallback(() => {
    return countryConfig.mileage_unit === 'miles' ? 'Mileage (miles)' : 'Mileage (km)';
  }, [countryConfig]);

  // Get price label
  const getPriceLabel = useCallback(() => {
    return `Price (${countryConfig.currency_symbol})`;
  }, [countryConfig]);

  // Context value
  const value = {
    // Current country
    country,
    countryConfig,
    setCountry,
    
    // All countries
    allCountries,
    
    // Currency (independent from country)
    currency,
    currencyConfig,
    setCurrency,
    allCurrencies: allCurrenciesList,
    
    // Field visibility
    enabledFields,
    disabledFields,
    requiredFields,
    isFieldEnabled,
    isFieldRequired,
    
    // Vehicle types for country
    vehicleTypes,
    
    // Filter schema
    filterSchema,
    
    // Formatting utilities
    formatCurrency,
    formatMileage,
    getMileageLabel,
    getPriceLabel,
    
    // Loading state
    loading,
    error,
    
    // Refresh
    refreshConfig: () => fetchCountryConfig(country)
  };

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  );
};

// Custom hook to use country context
export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export default CountryContext;
